import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { WhatsAppHeader } from '@/components/whatsapp/WhatsAppWorkspace';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Pagination } from '@/components/Pagination';
import { CampaignStatusBadge } from '@/components/campaigns/CampaignStatusBadge';
import { CampaignDeliveryChart } from '@/components/campaigns/CampaignDeliveryChart';
import { CampaignProgress } from '@/components/campaigns/CampaignProgress';
import { campaignsService, Campaign, Recipient } from '@/services/campaigns.service';
import { CampaignMessagePreview } from '@/components/campaigns/CampaignMessagePreview';
import { templatesService, Template } from '@/services/templates.service';
import {
  Play, Pause, XCircle, ArrowLeft, Send, Inbox, BookOpen, AlertTriangle, Users, ChevronDown, ChevronUp,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

/** Expandable failure reason for long messages */
const FailureReason: React.FC<{ reason: string }> = ({ reason }) => {
  const [expanded, setExpanded] = useState(false);
  const SHORT = 80;
  const isLong = reason.length > SHORT;
  if (!isLong) return <span className="text-xs text-danger">{reason}</span>;
  return (
    <div className="text-xs text-danger">
      {expanded ? reason : `${reason.slice(0, SHORT)}…`}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="ml-1 inline-flex items-center gap-0.5 rounded text-2xs font-semibold underline-offset-2 hover:underline"
        aria-label={expanded ? 'Show less' : 'Why?'}
      >
        {expanded ? <><ChevronUp size={10} />Less</> : <><ChevronDown size={10} />Why?</>}
      </button>
    </div>
  );
};

const recipientStatusMap: Record<string, { cls: string }> = {
  SENT:      { cls: 'bg-primary-soft text-primary' },
  DELIVERED: { cls: 'bg-info-soft text-info' },
  READ:      { cls: 'bg-success-soft text-success' },
  UNKNOWN:   { cls: 'bg-warning-soft text-warning' },
  FAILED:    { cls: 'bg-danger-soft text-danger' },
  PENDING:   { cls: 'bg-surface-alt text-text-muted' },
};

export default function CampaignDetail() {
  const { uid } = useParams<{ uid: string }>();
  const nav = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [recipients, setRecipients] = useState<{ items: Recipient[]; total: number; page: number; limit: number } | null>(null);
  const [page, setPage] = useState(1);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [template, setTemplate] = useState<Template | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const templateName = template?.name;
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const refresh = () => Promise.all([
    campaignsService.get(uid!).then(setCampaign),
    campaignsService.recipients(uid!, page, 25).then(setRecipients),
  ]);

  useEffect(() => {
    refresh();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [uid, page]);

  // Resolve the template's human name — the campaign doc only stores the uid.
  useEffect(() => {
    if (!campaign?.templateUid) return;
    let cancelled = false;
    setTemplate(null);
    setTemplateLoading(true);
    templatesService.get(campaign.templateUid)
      .then((t) => { if (!cancelled) setTemplate(t); })
      .catch(() => { /* Deleted templates may still have saved recipient snapshots. */ })
      .finally(() => { if (!cancelled) setTemplateLoading(false); });
    return () => { cancelled = true; };
  }, [campaign?.templateUid]);

  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (campaign?.status === 'RUNNING' || campaign?.status === 'SCHEDULED') {
      pollRef.current = setInterval(refresh, 3000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [campaign?.status]);

  const start  = async () => { try { const c = await campaignsService.start(uid!);  setCampaign(c); toast.success(c.status === 'SCHEDULED' ? 'Campaign scheduled' : 'Campaign started'); refresh(); } catch { /* toast shown */ } };
  const pause  = async () => { try { const c = await campaignsService.pause(uid!);  setCampaign(c); toast.success('Paused'); } catch { /* toast shown */ } };
  const resume = async () => { try { const c = await campaignsService.resume(uid!); setCampaign(c); toast.success('Resumed'); refresh(); } catch { /* toast shown */ } };
  const cancel = async () => { try { const c = await campaignsService.cancel(uid!); setCampaign(c); toast.success('Cancelled'); setConfirmCancel(false); } catch { /* toast shown */ } };

  if (!campaign) {
    return (
      <>
        <WhatsAppHeader section="campaigns" title="Campaign" />
        <div className="px-7 py-7 w-full space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}
        </div>
      </>
    );
  }

  return (
    <>
      <WhatsAppHeader section="campaigns"
        title={campaign.name}

        description={campaign.description ?? undefined}
        back={
          <button
            onClick={() => nav('/campaigns')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary transition-colors"
            aria-label="Back to campaigns"
          >
            <ArrowLeft size={16} />
          </button>
        }
        actions={
          <div className="flex items-center gap-2">
            <CampaignStatusBadge status={campaign.status} />
            {campaign.status === 'DRAFT' && (
              <Button iconLeft={<Play size={14} />} onClick={start}>{campaign.scheduleMode === 'scheduled' ? 'Schedule' : 'Start'}</Button>
            )}
            {campaign.status === 'RUNNING' && (
              <Button variant="secondary" iconLeft={<Pause size={14} />} onClick={pause}>Pause</Button>
            )}
            {campaign.status === 'PAUSED' && (
              <Button iconLeft={<Play size={14} />} onClick={resume}>Resume</Button>
            )}
            {(['RUNNING', 'PAUSED', 'SCHEDULED'] as const).includes(campaign.status as any) && (
              <Button variant="danger" iconLeft={<XCircle size={14} />} onClick={() => setConfirmCancel(true)}>Cancel</Button>
            )}
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[1680px] px-4 py-6 sm:px-7 sm:py-8">
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_370px]">
      <div className="min-w-0 space-y-5">
        {campaign.startError && <div role="alert" className="rounded-xl border border-danger/30 bg-danger-soft p-4 text-sm text-danger">Campaign could not start: {campaign.startError}</div>}
        {campaign.status === 'SCHEDULED' && campaign.scheduledAt && <div className="rounded-xl border border-border bg-primary-soft p-4 text-sm">Scheduled for {new Date(campaign.scheduledAt).toLocaleString()}. Messages will queue when this time is reached.</div>}
        {/* KPI tiles */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <KpiTile label="Total" value={campaign.totalRecipients} icon={<Users size={17} />} accent="gold" />
          <KpiTile label="Sent" value={campaign.sent} icon={<Send size={17} />} accent="gold" />
          <KpiTile label="Delivered" value={campaign.delivered} icon={<Inbox size={17} />} accent="gold" />
          <KpiTile label="Read" value={campaign.read} icon={<BookOpen size={17} />} accent="gold" />
        </div>

        {/* Progress card */}
        {!!campaign.unknown && <div className="mb-4 rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">{campaign.unknown} messages have unconfirmed delivery. Review actual delivery before retrying; the system will not resend them automatically.</div>}
          <CampaignDeliveryChart sent={campaign.sent} delivered={campaign.delivered} read={campaign.read} failed={campaign.failed} />
        <Card>
          <SectionHeader title="Progress" className="mb-4" />
          <CampaignProgress campaign={campaign} />
          {campaign.status === 'RUNNING' && (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-text-muted">
              <span className="inline-block h-2 w-2 rounded-full bg-primary animate-pulse" />
              Auto-refreshing every 3s
            </div>
          )}
        </Card>

        {/* Configuration */}
        <Card>
          <SectionHeader title="Configuration" className="mb-4" />
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoRow label="Template" value={
              <a href={`/templates/${campaign.templateUid}`} className="font-medium text-primary underline-offset-2 hover:underline">
                {templateName ?? <span className="font-mono">{campaign.templateUid.slice(-8)}</span>}
              </a>
            } />
            <InfoRow label="Language" value={campaign.language} />
            <InfoRow label="Schedule" value={`${campaign.scheduleMode}${campaign.scheduledAt ? ` @ ${new Date(campaign.scheduledAt).toLocaleString()}` : ''}`} />
            <InfoRow label="Started" value={campaign.startedAt ? new Date(campaign.startedAt).toLocaleString() : '—'} />
            {campaign.completedAt && (
              <InfoRow label="Completed" value={new Date(campaign.completedAt).toLocaleString()} />
            )}
          </div>
        </Card>

        {/* Failed banner — shows most-common failure reason when majority of recipients failed */}
        {campaign.failed > 0 && (() => {
          const failedItems = recipients?.items.filter((r) => r.status === 'FAILED' && r.failureReason) ?? [];
          const reasonCounts: Record<string, number> = {};
          for (const r of failedItems) {
            const key = r.failureReason!;
            reasonCounts[key] = (reasonCounts[key] ?? 0) + 1;
          }
          const topReason = Object.entries(reasonCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
          const topCount = topReason ? reasonCounts[topReason] : 0;
          const mostlySameReason = topCount > 0 && topCount >= failedItems.length * 0.5;

          return (
            <div className="flex flex-col gap-2 rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-sm">
              <div className="flex items-start gap-3">
                <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
                <div className="text-danger flex-1">
                  <span className="font-semibold">{campaign.failed.toLocaleString()} message{campaign.failed !== 1 ? 's' : ''} failed.</span>
                  {mostlySameReason && (
                    <>
                      {' '}Common Meta error: <span className="font-mono">{topReason}</span>
                      {topReason?.includes('Invalid parameter') || topReason?.includes('#100') ? (
                        <span className="ml-2 text-xs">
                          — This usually means the template ID is invalid. Try{' '}
                          <a href="/templates" className="underline font-semibold">Sync from Meta</a>{' '}
                          to pull your real approved templates.
                        </span>
                      ) : null}
                    </>
                  )}
                  {!mostlySameReason && (
                    <span className="ml-1">Check recipient rows below for individual failure reasons.</span>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Recipients */}
        <Card padded={false} className="overflow-hidden">
          <div className="border-b border-border px-5 py-4">
            <SectionHeader title="Recipients" className="mb-0" />
          </div>
          {!recipients ? (
            <div className="space-y-2 p-5">
              {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-10 rounded-lg" />)}
            </div>
          ) : recipients.items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-5 py-10 text-text-muted">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-alt">
                <Users size={18} />
              </div>
              <div className="text-sm">{campaign.status === 'SCHEDULED' ? 'Recipients will be prepared at the scheduled time.' : campaign.status === 'CANCELLED' ? 'This campaign was cancelled before recipients were queued.' : 'Recipients appear when the campaign starts.'}</div>
            </div>
          ) : (
            <div className="overflow-x-auto"><div className="min-w-[760px]">
              <div
                className="grid border-b border-border bg-surface-alt px-5 py-2.5"
                style={{ gridTemplateColumns: '160px 100px 1fr 1fr 1fr 1fr', gap: '12px' }}
              >
                {['Phone', 'Status', 'Sent', 'Delivered', 'Read', 'Failure'].map((h) => (
                  <span key={h} className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{h}</span>
                ))}
              </div>
              <div className="divide-y divide-border">
                {recipients.items.map((r, i) => {
                  const sm = recipientStatusMap[r.status] ?? { cls: 'bg-surface-alt text-text-muted' };
                  return (
                    <div
                      key={i}
                      className="grid items-center px-5 py-3 text-sm transition-colors hover:bg-surface-alt/60"
                      style={{ gridTemplateColumns: '160px 100px 1fr 1fr 1fr 1fr', gap: '12px' }}
                    >
                      <span className="font-mono text-xs text-text-secondary">{r.phone}</span>
                      {/* w-fit + justify-self-start — a grid cell blockifies the span and stretched the pill to the full column */}
                      <span className={clsx('inline-flex w-fit justify-self-start items-center rounded-full px-2 py-0.5 text-2xs font-semibold tracking-wide', sm.cls)}>
                        {r.status}
                      </span>
                      <span className="text-xs text-text-muted">{r.sentAt ? new Date(r.sentAt).toLocaleString() : '—'}</span>
                      <span className="text-xs text-text-muted">{r.deliveredAt ? new Date(r.deliveredAt).toLocaleString() : '—'}</span>
                      <span className="text-xs text-text-muted">{r.readAt ? new Date(r.readAt).toLocaleString() : '—'}</span>
                      <div>{r.failureReason ? <FailureReason reason={r.failureReason} /> : <span className="text-xs text-text-muted">—</span>}</div>
                    </div>
                  );
                })}
              </div>
              <div className="px-5 pb-2">
                <Pagination page={recipients.page} total={recipients.total} limit={recipients.limit} onChange={setPage} />
              </div>
            </div></div>
          )}
        </Card>
      </div>
      <CampaignMessagePreview campaign={campaign} template={template} recipients={recipients?.items ?? []} loading={templateLoading} />
      </div>
      </div>

      <ConfirmDialog
        open={confirmCancel}
        title="Cancel campaign?"
        message={`Cancel "${campaign.name}"? In-flight messages will still complete; queued ones stop.`}
        danger
        onConfirm={cancel}
        onCancel={() => setConfirmCancel(false)}
      />
    </>
  );
}

const KpiTile: React.FC<{ label: string; value: number; icon: React.ReactNode; accent: 'gold' | 'green' | 'blue' | 'amber' | 'red' }> = ({ label, value, icon, accent }) => {
  const accentMap = {
    gold:  'bg-primary-soft text-primary',
    green: 'bg-success-soft text-success',
    blue:  'bg-info-soft text-info',
    amber: 'bg-warning-soft text-warning',
    red:   'bg-danger-soft text-danger',
  };
  return (
    <div className="rounded-2xl border border-border bg-surface p-5 shadow-soft">
      <div className="flex items-start justify-between gap-2">
        <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{label}</div>
        <div className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', accentMap[accent])}>
          {icon}
        </div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold tracking-tight text-text-primary tabular-nums">
        {value.toLocaleString()}
      </div>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <span className="text-text-muted">{label}: </span>
    <span className="text-text-primary">{value}</span>
  </div>
);
